import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { MapPinned, Link2, Unlink } from "lucide-react";
import { api, errorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { MappingCsv } from "@/pages/admin/AdminLayers";

const LEVELS = [["district", "District"], ["tehsil", "Tehsil"], ["ri", "R.I. Circle"], ["village", "Village"]];
const EMPTY = { district: "", tehsil: "", ri: "", village: "" };
const pathOf = (l) => LEVELS.map(([k]) => l[k]).filter(Boolean).join(" › ");

// Cascading District › Tehsil › R.I. › Village selects fed by the Locations master list (same as the on-map panel).
function LocationCascade({ locations, value, onChange }) {
  const opts = (level) => {
    const i = LEVELS.findIndex(([k]) => k === level);
    const rows = locations.filter((r) => LEVELS.slice(0, i).every(([k]) => !value[k] || r[k] === value[k]));
    return [...new Set(rows.map((r) => r[level]).filter(Boolean))].sort();
  };
  const set = (level, v) => {
    const i = LEVELS.findIndex(([k]) => k === level);
    const next = { ...value, [level]: v };
    LEVELS.slice(i + 1).forEach(([k]) => { next[k] = ""; });
    onChange(next);
  };
  return (
    <div className="grid sm:grid-cols-4 gap-3">
      {LEVELS.map(([k, label], i) => {
        const prev = i === 0 ? null : LEVELS[i - 1];
        const disabled = prev && !value[prev[0]];
        return (
          <label key={k} className="text-xs font-medium space-y-1">
            <span>{label}</span>
            <select data-testid={`file-mapping-${k}`} value={value[k]} disabled={disabled} onChange={(e) => set(k, e.target.value)} className="w-full h-10 rounded-xl border bg-background px-3 text-sm disabled:opacity-50">
              <option value="">{disabled ? `Select ${prev[1].toLowerCase()} first` : `All ${label.toLowerCase()}s`}</option>
              {opts(k).map((v) => <option key={v} value={v}>{v}</option>)}
            </select>
          </label>
        );
      })}
    </div>
  );
}

// File Mapping control (from the user's Bolt "FileMapping"): link library files to a location so the map dropdowns filter them.
export function AdminFileMapping() {
  const [locations, setLocations] = useState([]);
  const [layers, setLayers] = useState([]);
  const [sel, setSel] = useState(EMPTY);
  const [picked, setPicked] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = () => Promise.all([api.get("/admin/locations"), api.get("/admin/layers")]).then(([a, b]) => { setLocations(a.data); setLayers(b.data); }).catch((err) => toast.error(errorMessage(err)));
  useEffect(() => { load(); }, []);

  const hasSel = !!sel.district;
  const linked = useMemo(() => layers.filter((l) => hasSel && LEVELS.every(([k]) => !sel[k] || l[k] === sel[k]) && LEVELS.every(([k]) => !!sel[k] === !!l[k] || !sel[k])), [layers, sel, hasSel]);
  const available = layers.filter((l) => !linked.some((x) => x.id === l.id));
  const mappedLayers = layers.filter((l) => l.district);

  const link = async () => {
    if (!hasSel || !picked.length) return;
    setBusy(true);
    try {
      await Promise.all(picked.map((id) => api.patch(`/admin/layers/${id}`, sel)));
      toast.success(`${picked.length} file${picked.length === 1 ? "" : "s"} linked to ${pathOf(sel)}`);
      setPicked([]); await load();
    } catch (err) { toast.error(errorMessage(err)); } finally { setBusy(false); }
  };
  const unlink = async (l) => {
    try { await api.patch(`/admin/layers/${l.id}`, EMPTY); toast.success(`"${l.name}" unlinked`); await load(); } catch (err) { toast.error(errorMessage(err)); }
  };

  return (
    <div data-testid="admin-file-mapping" className="space-y-6">
      <section className="rounded-2xl border bg-card p-6 space-y-5">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="flex-1">
            <p className="font-semibold flex items-center gap-2"><MapPinned size={16} /> File mapping</p>
            <p className="text-xs text-muted-foreground">Choose a location, then tick the library files that belong to it. The map's Location dropdown shows only files linked to the chosen area. {locations.length === 0 && <span className="text-amber-700">Add districts/villages under the Locations tab first.</span>}</p>
          </div>
          <MappingCsv onDone={load} />
        </div>
        <LocationCascade locations={locations} value={sel} onChange={(v) => { setSel(v); setPicked([]); }} />
        {hasSel && (
          <div className="grid lg:grid-cols-2 gap-4">
            <div className="rounded-xl border p-4">
              <p className="text-xs font-medium mb-2 flex items-center gap-1"><Link2 size={12} /> Linked to {pathOf(sel)} <span className="text-muted-foreground">· {linked.length}</span></p>
              {linked.length === 0 && <p className="text-xs text-muted-foreground italic" data-testid="file-mapping-linked-empty">No files linked yet.</p>}
              <ul className="space-y-1">
                {linked.map((l) => (
                  <li key={l.id} data-testid={`file-mapping-linked-${l.id}`} className="flex items-center gap-2 text-sm p-2 rounded-lg bg-muted/50">
                    <span className="w-3 h-3 rounded-sm shrink-0" style={{ background: l.color }} /><span className="flex-1 truncate">{l.alias || l.name}</span>
                    <span className="text-[10px] font-mono text-muted-foreground truncate max-w-[140px]">{l.original_filename}</span>
                    <button type="button" data-testid={`file-mapping-unlink-${l.id}`} onClick={() => unlink(l)} title="Unlink" className="p-1 rounded text-red-600 hover:bg-red-50"><Unlink size={13} /></button>
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-xl border p-4 flex flex-col">
              <p className="text-xs font-medium mb-2">Library files <span className="text-muted-foreground">· tick to link</span></p>
              <ul className="space-y-1 flex-1 max-h-72 overflow-auto">
                {available.map((l) => (
                  <li key={l.id}>
                    <label data-testid={`file-mapping-pick-${l.id}`} className="flex items-center gap-2 text-sm p-2 rounded-lg hover:bg-muted cursor-pointer">
                      <input type="checkbox" className="accent-emerald-700 w-4 h-4" checked={picked.includes(l.id)} onChange={(e) => setPicked((p) => (e.target.checked ? [...p, l.id] : p.filter((x) => x !== l.id)))} />
                      <span className="w-3 h-3 rounded-sm shrink-0" style={{ background: l.color }} /><span className="flex-1 truncate">{l.alias || l.name}</span>
                      {l.district && <span className="text-[10px] text-muted-foreground truncate max-w-[150px]" title={pathOf(l)}>{pathOf(l)}</span>}
                    </label>
                  </li>
                ))}
                {available.length === 0 && <li className="text-xs text-muted-foreground italic p-2">All files are already linked here.</li>}
              </ul>
              <Button type="button" data-testid="file-mapping-link-button" disabled={!picked.length || busy} onClick={link} className="mt-3 rounded-full bg-emerald-900 hover:bg-emerald-800 dark:bg-emerald-500 dark:text-slate-950"><Link2 size={14} className="mr-1" /> {busy ? "Linking…" : `Link ${picked.length || ""} file${picked.length === 1 ? "" : "s"}`}</Button>
            </div>
          </div>
        )}
      </section>

      <section className="rounded-2xl border bg-card overflow-hidden">
        <div className="p-4 border-b"><p className="font-semibold text-sm">All mappings</p><p className="text-xs text-muted-foreground" data-testid="file-mapping-count">{mappedLayers.length} of {layers.length} files linked to a location</p></div>
        <Table>
          <TableHeader><TableRow><TableHead>District</TableHead><TableHead>Tehsil</TableHead><TableHead>R.I.</TableHead><TableHead>Village</TableHead><TableHead>File</TableHead><TableHead /></TableRow></TableHeader>
          <TableBody>
            {mappedLayers.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-sm text-muted-foreground py-8">No files mapped yet.</TableCell></TableRow>}
            {mappedLayers.map((l) => (
              <TableRow key={l.id} data-testid={`file-mapping-row-${l.id}`}>
                {LEVELS.map(([k]) => <TableCell key={k} className="text-sm">{l[k] || <span className="text-muted-foreground">—</span>}</TableCell>)}
                <TableCell className="text-sm"><div className="flex items-center gap-2"><span className="w-3 h-3 rounded-sm shrink-0" style={{ background: l.color }} /><span className="truncate max-w-[200px]">{l.alias || l.name}</span><span className="text-[10px] font-mono text-muted-foreground truncate max-w-[140px]">{l.original_filename}</span></div></TableCell>
                <TableCell className="text-right"><button type="button" onClick={() => unlink(l)} data-testid={`file-mapping-row-unlink-${l.id}`} className="p-1.5 rounded-full text-red-600 hover:bg-red-50" title="Unlink"><Unlink size={14} /></button></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </section>
    </div>
  );
}
